import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Switch,
  FormControlLabel,
  Snackbar,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { endpoints } from '../../services/api';
import PermissionDialog from '../../components/Permisos/PermissionDialog';

// Tipos de usuario disponibles
const TIPOS_USUARIO = [
  { value: 'admin', label: 'Administrador', color: 'error' },
  { value: 'supervisor', label: 'Supervisor', color: 'warning' },
  { value: 'paletizador', label: 'Paletizador', color: 'primary' },
  { value: 'distribuidor', label: 'Distribuidor', color: 'secondary' },
  { value: 'recepcion', label: 'Recepción', color: 'info' },
  { value: 'facturacion', label: 'Facturación', color: 'success' },
];

// Estado inicial del formulario
const initialFormState = {
  nombre: '',
  email: '',
  password: '',
  cargo: '',
  tipo: 'paletizador',
  base_estacion: 'MIA',
  active: true,
};

const Usuarios = () => {
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Obtener usuarios
  const { data: usuarios, isLoading, error, refetch } = useQuery({
    queryKey: ['usuarios'],
    queryFn: async () => {
      const response = await api.get(endpoints.usuarios.list);
      return response.data.data?.usuarios || response.data.data || [];
    },
  });

  // Mutation para crear usuario
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.usuarios.create, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['usuarios']);
      setUserDialogOpen(false);
      setFormData(initialFormState);
      setSnackbar({ open: true, message: 'Usuario creado exitosamente', severity: 'success' });
    },
    onError: (error) => {
      const message = error.response?.data?.message || 'Error al crear usuario';
      setSnackbar({ open: true, message, severity: 'error' });
    },
  });

  // Mutation para actualizar usuario
  const updateMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.put(endpoints.usuarios.update, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['usuarios']);
      setUserDialogOpen(false);
      setFormData(initialFormState);
      setIsEditing(false);
      setSnackbar({ open: true, message: 'Usuario actualizado exitosamente', severity: 'success' });
    },
    onError: (error) => {
      const message = error.response?.data?.message || 'Error al actualizar usuario';
      setSnackbar({ open: true, message, severity: 'error' });
    },
  });

  // Mutation para eliminar usuario
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`${endpoints.usuarios.delete}?id=${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['usuarios']);
      setSnackbar({ open: true, message: 'Usuario desactivado exitosamente', severity: 'success' });
    },
    onError: (error) => {
      const message = error.response?.data?.message || 'Error al eliminar usuario';
      setSnackbar({ open: true, message, severity: 'error' });
    },
  });

  const handleManagePermissions = (usuario) => {
    setSelectedUser(usuario);
    setPermissionDialogOpen(true);
  };

  const handleClosePermissionDialog = () => {
    setPermissionDialogOpen(false);
    setSelectedUser(null);
    refetch();
  };

  const handleOpenCreateDialog = () => {
    setFormData(initialFormState);
    setIsEditing(false);
    setUserDialogOpen(true);
  };

  const handleOpenEditDialog = (usuario) => {
    setFormData({
      id: usuario.id,
      nombre: usuario.nombre || '',
      email: usuario.email || '',
      password: '', // No mostrar password actual
      cargo: usuario.cargo || '',
      tipo: usuario.tipo || 'paletizador',
      base_estacion: usuario.base_estacion || 'MIA',
      active: usuario.active === 1 || usuario.active === true,
    });
    setIsEditing(true);
    setUserDialogOpen(true);
  };

  const handleCloseUserDialog = () => {
    setUserDialogOpen(false);
    setFormData(initialFormState);
    setIsEditing(false);
  };

  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = () => {
    // Validaciones básicas
    if (!formData.nombre.trim()) {
      setSnackbar({ open: true, message: 'El nombre es requerido', severity: 'error' });
      return;
    }
    if (!formData.email.trim()) {
      setSnackbar({ open: true, message: 'El email es requerido', severity: 'error' });
      return;
    }
    if (!isEditing && !formData.password.trim()) {
      setSnackbar({ open: true, message: 'La contraseña es requerida para nuevos usuarios', severity: 'error' });
      return;
    }

    const dataToSend = {
      ...formData,
      active: formData.active ? 1 : 0,
    };

    // Si es edición y no se cambió el password, no enviarlo
    if (isEditing && !formData.password.trim()) {
      delete dataToSend.password;
    }

    if (isEditing) {
      updateMutation.mutate(dataToSend);
    } else {
      createMutation.mutate(dataToSend);
    }
  };

  const handleDelete = (usuario) => {
    if (window.confirm(`¿Está seguro de desactivar al usuario "${usuario.nombre}"?`)) {
      deleteMutation.mutate(usuario.id);
    }
  };

  const getTipoInfo = (tipo) => {
    return TIPOS_USUARIO.find(t => t.value === tipo) || { label: tipo || 'Sin tipo', color: 'default' };
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Error al cargar usuarios: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Gestión de Usuarios
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Nuevo Usuario
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Cargo</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Base</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usuarios && usuarios.map((usuario) => {
                const tipoInfo = getTipoInfo(usuario.tipo);
                return (
                  <TableRow key={usuario.id} hover>
                    <TableCell>{usuario.nombre}</TableCell>
                    <TableCell>{usuario.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={usuario.cargo || 'Sin cargo'} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={tipoInfo.label} 
                        size="small" 
                        color={tipoInfo.color}
                      />
                    </TableCell>
                    <TableCell>{usuario.base_estacion || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={usuario.active ? 'Activo' : 'Inactivo'} 
                        size="small" 
                        color={usuario.active ? 'success' : 'default'}
                        variant={usuario.active ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenEditDialog(usuario)}
                        title="Editar Usuario"
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="secondary"
                        onClick={() => handleManagePermissions(usuario)}
                        title="Gestionar Permisos"
                        size="small"
                      >
                        <SettingsIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(usuario)}
                        title="Desactivar Usuario"
                        size="small"
                        disabled={deleteMutation.isLoading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {(!usuarios || usuarios.length === 0) && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No hay usuarios registrados
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Diálogo de Crear/Editar Usuario */}
      <Dialog 
        open={userDialogOpen} 
        onClose={handleCloseUserDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}
            <IconButton onClick={handleCloseUserDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleFormChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleFormChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={isEditing ? "Nueva Contraseña (dejar vacío para mantener)" : "Contraseña"}
                name="password"
                type="password"
                value={formData.password}
                onChange={handleFormChange}
                required={!isEditing}
                helperText={isEditing ? "Solo complete si desea cambiar la contraseña" : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cargo"
                name="cargo"
                value={formData.cargo}
                onChange={handleFormChange}
                placeholder="Ej: Supervisor, Operador..."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleFormChange}
                  label="Tipo"
                >
                  {TIPOS_USUARIO.map((tipo) => (
                    <MenuItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Base/Estación"
                name="base_estacion"
                value={formData.base_estacion}
                onChange={handleFormChange}
                placeholder="Ej: MIA, BOG, LAX..."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    name="active"
                    checked={formData.active}
                    onChange={handleFormChange}
                    color="success"
                  />
                }
                label={formData.active ? "Usuario Activo" : "Usuario Inactivo"}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseUserDialog} color="inherit">
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={createMutation.isLoading || updateMutation.isLoading}
          >
            {createMutation.isLoading || updateMutation.isLoading ? (
              <CircularProgress size={24} />
            ) : isEditing ? 'Guardar Cambios' : 'Crear Usuario'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Permisos */}
      {selectedUser && (
        <PermissionDialog
          open={permissionDialogOpen}
          onClose={handleClosePermissionDialog}
          usuario={selectedUser}
        />
      )}

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Usuarios;
